import { ref, readonly, type Ref } from 'vue';
import { ENV_CONFIG } from '@/config/constants';
import { isWordDefinitions, type WordDefinitions } from '@/types/word';
import STRINGS from '@/constants/strings';

/**
 * Fetches a word's dictionary entry from `GET /api/word/{word}` (issue #354).
 *
 * Mirrors the fetch/guard/loading conventions of `useProfile.ts`:
 * `ENV_CONFIG.getApiBaseUrl()` for the base, a runtime type guard on the body,
 * and a `try/catch/finally` that owns `isLoading` + an error ref.
 *
 * The endpoint is a public read (no `credentials`). A missing word is NOT an
 * error — the server returns HTTP 200 with an empty `records` array (a coverage
 * gap). Only a network failure, a non-OK status, or an unparseable/invalid body
 * sets `error`. Each instance keeps its own state, so a modal can hold one.
 */
export function useWordDefinition() {
  const definition = ref<WordDefinitions | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Guards against a slow earlier request overwriting a newer one when the user
  // opens the modal on word A then quickly switches to word B.
  let latestRequestId = 0;

  async function load(word: string): Promise<void> {
    const requestId = ++latestRequestId;
    isLoading.value = true;
    error.value = null;
    definition.value = null;

    try {
      const response = await fetch(
        `${ENV_CONFIG.getApiBaseUrl()}/api/word/${encodeURIComponent(word)}`
      );

      if (!response.ok) {
        throw new Error(`Definition lookup failed (status ${response.status})`);
      }

      let json: unknown;
      try {
        json = await response.json();
      } catch {
        throw new Error('Server returned an invalid response');
      }

      if (!isWordDefinitions(json)) {
        throw new Error('Server returned an invalid response');
      }

      // Discard if a newer request has since started.
      if (requestId !== latestRequestId) return;
      definition.value = json;
    } catch {
      if (requestId !== latestRequestId) return;
      error.value = STRINGS.definitions.error;
    } finally {
      if (requestId === latestRequestId) {
        isLoading.value = false;
      }
    }
  }

  function reset(): void {
    // Bump the request id so any in-flight load resolves into a no-op.
    latestRequestId++;
    definition.value = null;
    isLoading.value = false;
    error.value = null;
  }

  return {
    definition: readonly(definition) as Readonly<Ref<WordDefinitions | null>>,
    isLoading: readonly(isLoading) as Readonly<Ref<boolean>>,
    error: readonly(error) as Readonly<Ref<string | null>>,
    load,
    reset,
  };
}
